import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../services/apiService";
import Loader from "../../elements/Loader";

/* ── filter catalogue ────────────────────────────────────────────── */
const FILTER_TYPES = [
  { value: 'allUsers',         label: 'All Users',                  group: 'General',      inputType: 'none', noneLabel: 'All registered app users' },
  { value: 'minBookings',      label: 'Min Bookings Completed',     group: 'Booking',      inputType: 'number', placeholder: 'e.g. 2' },
  { value: 'minSpent',         label: 'Min Amount Spent (₹)',       group: 'Booking',      inputType: 'number', placeholder: 'e.g. 5000' },
  { value: 'lastBookingDays',  label: 'Had Booking in Last N Days', group: 'Booking',      inputType: 'number', placeholder: 'e.g. 30' },
  { value: 'noBookingDays',    label: 'No Booking in Last N Days',  group: 'Booking',      inputType: 'number', placeholder: 'e.g. 60' },
  { value: 'registeredAfter',  label: 'Registered After',           group: 'User Account', inputType: 'date' },
  { value: 'registeredBefore', label: 'Registered Before',          group: 'User Account', inputType: 'date' },
  { value: 'isExistingUser',   label: 'App User',                   group: 'User Account', inputType: 'select', options: [{ value: 'true', label: 'Yes — has app account' }, { value: 'false', label: 'No — lead only' }] },
  { value: 'allLeads',         label: 'All TeleCRM Leads',          group: 'TeleCRM',      inputType: 'none', noneLabel: 'Every TeleCRM lead (whether or not they later signed up)' },
  { value: 'leadStatus',       label: 'TeleCRM Lead Status',        group: 'TeleCRM',      inputType: 'dynamic-select', dynamicKey: 'leadStatuses' },
  { value: 'city',             label: 'City (contains)',            group: 'TeleCRM',      inputType: 'text',   placeholder: 'e.g. Mumbai' },
  { value: 'facebookCampaign', label: 'Facebook Campaign',          group: 'TeleCRM',      inputType: 'dynamic-select', dynamicKey: 'facebookCampaigns' },
  { value: 'pujaInterest',     label: 'Puja Interest',              group: 'TeleCRM',      inputType: 'dynamic-select', dynamicKey: 'pujas' },
];

const TYPE_MAP = Object.fromEntries(FILTER_TYPES.map(f => [f.value, f]));

const EMPTY_FILTER = { type: 'allUsers', value: '' };

function FilterRow({ filter, index, onChange, onRemove, usedTypes, dynamicOptions }) {
  const def = TYPE_MAP[filter.type] || FILTER_TYPES[0];

  const dynamicOpts = def.inputType === 'dynamic-select'
    ? (dynamicOptions[def.dynamicKey] || [])
    : [];

  return (
    <div className="d-flex align-items-start gap-2 mb-2" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: 8 }}>
      <div style={{ flex: '0 0 260px' }}>
        <select
          className="form-control form-control-sm"
          value={filter.type}
          onChange={e => onChange(index, { type: e.target.value, value: '' })}
        >
          {Object.entries(
            FILTER_TYPES.reduce((acc, f) => {
              (acc[f.group] = acc[f.group] || []).push(f);
              return acc;
            }, {})
          ).map(([grp, opts]) => (
            <optgroup key={grp} label={grp}>
              {opts.map(o => (
                <option key={o.value} value={o.value}
                  disabled={usedTypes.includes(o.value) && o.value !== filter.type}
                >
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div style={{ flex: 1 }}>
        {def.inputType === 'none' && (
          <input className="form-control form-control-sm" value={def.noneLabel || 'Matches everyone'} disabled />
        )}
        {def.inputType === 'number' && (
          <input
            type="number" min="0"
            className="form-control form-control-sm"
            placeholder={def.placeholder}
            value={filter.value}
            onChange={e => onChange(index, { ...filter, value: e.target.value })}
          />
        )}
        {def.inputType === 'text' && (
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={def.placeholder}
            value={filter.value}
            onChange={e => onChange(index, { ...filter, value: e.target.value })}
          />
        )}
        {def.inputType === 'date' && (
          <input
            type="date"
            className="form-control form-control-sm"
            value={filter.value}
            onChange={e => onChange(index, { ...filter, value: e.target.value })}
          />
        )}
        {def.inputType === 'select' && (
          <select
            className="form-control form-control-sm"
            value={filter.value}
            onChange={e => onChange(index, { ...filter, value: e.target.value })}
          >
            <option value="">Select…</option>
            {def.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        {def.inputType === 'dynamic-select' && (
          <select
            className="form-control form-control-sm"
            value={filter.value}
            onChange={e => onChange(index, { ...filter, value: e.target.value })}
          >
            <option value="">
              {dynamicOpts.length === 0 ? 'Loading…' : 'Select…'}
            </option>
            {dynamicOpts.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
      </div>

      <button
        type="button"
        className="btn btn-danger btn-xs"
        style={{ marginTop: 2, flexShrink: 0 }}
        onClick={() => onRemove(index)}
        title="Remove filter"
      >
        ✕
      </button>
    </div>
  );
}

export default function AddSegment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [groups, setGroups]     = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({ facebookCampaigns: [], pujas: [], leadStatuses: [] });

  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [groupName,   setGroupName]   = useState('');
  const [newGroup,    setNewGroup]    = useState('');
  const [filters,     setFilters]     = useState([{ ...EMPTY_FILTER }]);

  /* load existing groups from all segments */
  const fetchGroups = useCallback(async () => {
    try {
      const res = await ApiService.get('promotion/segment');
      const gNames = [...new Set((res.data || []).map(s => s.groupName).filter(Boolean))].sort();
      setGroups(gNames);
    } catch { /* non-critical */ }
  }, []);

  /* load dynamic filter options (facebook campaigns, puja list) */
  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await ApiService.get('promotion/segment-filter-options');
      if (res?.data) setDynamicOptions(res.data);
    } catch { /* non-critical */ }
  }, []);

  const fetchSegment = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.get(`promotion/segment/${id}`);
      if (res?.data) {
        const { name, description, criteria, groupName } = res.data;
        setName(name || '');
        setDescription(description || '');
        setGroupName(groupName || '');
        // Convert flat criteria object back into filter rows
        const rows = [];
        Object.entries(criteria || {}).forEach(([type, value]) => {
          if (TYPE_MAP[type]) rows.push({ type, value: value == null ? '' : String(value) });
        });
        setFilters(rows.length ? rows : [{ ...EMPTY_FILTER }]);
      }
    } catch { toast.error('Failed to load segment'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    fetchGroups();
    fetchFilterOptions();
    if (id) fetchSegment();
  }, [id, fetchGroups, fetchFilterOptions, fetchSegment]);

  const addFilter = () => {
    const used = filters.map(f => f.type);
    const next = FILTER_TYPES.find(f => !used.includes(f.value));
    setFilters(prev => [...prev, { type: next?.value || 'city', value: '' }]);
  };

  const updateFilter = (i, val) => setFilters(prev => prev.map((f, idx) => idx === i ? val : f));
  const removeFilter = (i) => setFilters(prev => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!filters.length) { toast.error('Add at least one filter'); return; }

    // Validate filter values
    for (const f of filters) {
      const def = TYPE_MAP[f.type];
      if (def?.inputType !== 'none' && !f.value) {
        toast.error(`Please set a value for "${def?.label}"`);
        return;
      }
    }

    // Build flat criteria object
    const criteria = {};
    filters.forEach(f => {
      criteria[f.type] = (f.type === 'allUsers' || f.type === 'allLeads') ? true : f.value;
    });

    const resolvedGroup = groupName === '__new__' ? newGroup.trim() : groupName;

    const payload = { name: name.trim(), description, criteria, groupName: resolvedGroup || null };

    setSaving(true);
    try {
      if (id) {
        await ApiService.put(`promotion/segment/${id}`, payload);
        toast.success('Segment updated');
      } else {
        await ApiService.post('promotion/segment', payload);
        toast.success('Segment created');
      }
      navigate('/segment', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save segment');
    } finally {
      setSaving(false);
    }
  };

  const usedTypes = filters.map(f => f.type);

  return (
    <div className="row">
      {loading && <Loader />}
      <div className="col-xl-8 col-lg-12 offset-md-2">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">{id ? 'Edit' : 'Add'} Segment</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>

              {/* Name + Group side by side */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Segment Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text" className="form-control"
                    placeholder="e.g. High-Value Mumbai Leads"
                    value={name} onChange={e => setName(e.target.value)}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Group / Folder</label>
                  <select
                    className="form-control"
                    value={groupName}
                    onChange={e => { setGroupName(e.target.value); setNewGroup(''); }}
                  >
                    <option value="">No group (ungrouped)</option>
                    {groups.map(g => <option key={g} value={g}>{g}</option>)}
                    <option value="__new__">+ Create new group…</option>
                  </select>
                  {groupName === '__new__' && (
                    <input
                      type="text" className="form-control mt-2"
                      placeholder="New group name…"
                      value={newGroup} onChange={e => setNewGroup(e.target.value)}
                    />
                  )}
                  <small className="text-muted">Groups organise segments into folders in the list.</small>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control" rows={2}
                  placeholder="Optional notes about this segment"
                  value={description} onChange={e => setDescription(e.target.value)}
                />
              </div>

              {/* Filter rules */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold mb-0">
                    Filters <span className="text-danger">*</span>
                    <small className="text-muted fw-normal ms-2">All filters are AND-combined</small>
                  </label>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={addFilter}
                    disabled={filters.length >= FILTER_TYPES.length}>
                    + Add Filter
                  </button>
                </div>

                {filters.map((f, i) => (
                  <FilterRow
                    key={i} filter={f} index={i}
                    onChange={updateFilter} onRemove={removeFilter}
                    usedTypes={usedTypes} dynamicOptions={dynamicOptions}
                  />
                ))}

                {/* Summary preview */}
                <div className="alert alert-info py-2 mt-2 mb-0" style={{ fontSize: 12 }}>
                  <i className="fa fa-info-circle me-1" />
                  Users must match <strong>all</strong> filters to be added to this segment.
                  Click <strong>Assign Users</strong> on the segment list after saving to populate it.
                </div>
              </div>

              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : id ? 'Update Segment' : 'Save Segment'}
                </button>
                <button type="button" className="btn btn-outline-secondary"
                  onClick={() => navigate('/segment', { replace: true })}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
